/**
 * Retry distribution donut, shared by the Statistics page, each endpoint's
 * Analytics tab and a stress run's detail page.
 *
 * Providers retry transient failures (429s, 503s, timeouts) internally, so a
 * call that looks like a clean success can still have cost several attempts.
 * This is the only place that shows how often that happens.
 */
import { useMemo } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import type { ApiRetryGroup } from '../types';

// Green for a first-attempt success, warming towards red as the attempts pile
// up. Anything past the palette reuses its last (worst) colour.
const RETRY_COLORS = ['#70B77E', '#F59E0B', '#F97316', '#EF4444', '#B91C1C'];

function colorFor(retries: number): string {
  return RETRY_COLORS[Math.min(retries, RETRY_COLORS.length - 1)];
}

function labelFor(retries: number): string {
  if (retries === 0) return 'No retries';
  return `${retries} ${retries === 1 ? 'retry' : 'retries'}`;
}

interface RetriesPanelProps {
  groups: ApiRetryGroup[] | undefined;
  loading?: boolean;
  /** Why there is nothing to show, when the window holds no measured calls. */
  emptyLabel?: string;
}

export function RetriesPanel({
  groups,
  loading = false,
  emptyLabel = 'No retry data in this period',
}: RetriesPanelProps) {
  const slices = useMemo(
    () =>
      (groups || [])
        .filter((group) => group.count > 0)
        .map((group) => ({
          key: String(group.retries),
          name: labelFor(group.retries),
          value: group.count,
          color: colorFor(group.retries),
        })),
    [groups],
  );

  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  const retried = slices
    .filter((slice) => slice.key !== '0')
    .reduce((sum, slice) => sum + slice.value, 0);

  return (
    <div className="rounded-xl border border-slate-200 shadow-soft p-4 bg-white flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Retries</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">
            Attempts a call needed before it settled.
          </p>
        </div>
        <div className="text-right shrink-0">
          <span className="text-lg font-bold text-slate-700 tabular-nums">
            {total > 0 ? `${((retried / total) * 100).toFixed(0)}%` : '—'}
          </span>
          <span className="ml-1 text-[10px] text-slate-500 uppercase">retried</span>
        </div>
      </div>

      {loading && total === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-slate-500 py-8">
          Loading…
        </div>
      ) : total === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center py-8 text-sm">
          <span className="font-medium text-slate-400">Nothing measured</span>
          <span className="text-xs text-slate-400 mt-0.5">{emptyLabel}</span>
        </div>
      ) : (
        <>
          <div className="h-44">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="52%"
                  outerRadius="80%"
                  paddingAngle={2}
                  stroke="none"
                >
                  {slices.map((slice) => (
                    <Cell key={slice.key} fill={slice.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    fontSize: '12px',
                  }}
                  formatter={(value: number, name: string) => [
                    `${value} call${value === 1 ? '' : 's'} · ${((value / total) * 100).toFixed(1)}%`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-3 space-y-1.5">
            {slices.map((slice) => (
              <li key={slice.key} className="flex items-center gap-2 text-xs">
                <span
                  className="w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ backgroundColor: slice.color }}
                />
                <span className="text-slate-600 truncate flex-1">{slice.name}</span>
                <span className="tabular-nums text-slate-500">
                  {((slice.value / total) * 100).toFixed(0)}%
                </span>
                <span className="tabular-nums font-medium text-slate-700 w-10 text-right">
                  {slice.value}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
